import { useEffect, useMemo, useRef, useState } from "react";
import { createCarouselSwipeHandlers } from "../gestures/createCarouselSwipeHandlers";
import { createCarouselLongPressHandlers } from "../gestures/createCarouselLongPressHandlers";

/**
 * React adapter for dashboard carousel gestures: horizontal swipe navigation
 * and long-press / right-click to open the Dashboard Navigator.
 * Attaches non-passive touchmove so horizontal commits can call preventDefault.
 *
 * On tablet/desktop (≥1024px), horizontal swipe is disabled — the KPI
 * workspace grid replaces carousel browsing. Long-press / right-click
 * navigator stays available.
 */
export function useDashboardCarouselSwipe({
  onNavigate,
  onOpenNavigator,
  enabled = true,
}) {
  const ref = useRef(null);
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;

  const onOpenNavigatorRef = useRef(onOpenNavigator);
  onOpenNavigatorRef.current = onOpenNavigator;

  const [isDesktopViewport, setIsDesktopViewport] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const media = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsDesktopViewport(Boolean(media.matches));
    sync();

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }

    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  const swipeHandlers = useMemo(
    () =>
      createCarouselSwipeHandlers({
        onSwipe: (direction) => onNavigateRef.current?.(direction),
      }),
    [],
  );

  const longPressHandlers = useMemo(
    () =>
      createCarouselLongPressHandlers({
        onLongPress: () => onOpenNavigatorRef.current?.(),
      }),
    [],
  );

  const navigatorEnabled = Boolean(onOpenNavigator);
  // Tablet/desktop use the workspace grid instead of horizontal swipe.
  const swipeEnabled = enabled && !isDesktopViewport;
  const gesturesActive = swipeEnabled || navigatorEnabled;

  useEffect(() => {
    const el = ref.current;
    if (!el || !gesturesActive) {
      return undefined;
    }

    const {
      onTouchStart: swipeTouchStart,
      onTouchMove: swipeTouchMove,
      onTouchEnd: swipeTouchEnd,
      onTouchCancel: swipeTouchCancel,
    } = swipeHandlers;

    const {
      onTouchStart: longPressTouchStart,
      onTouchMove: longPressTouchMove,
      onTouchEnd: longPressTouchEnd,
      onTouchCancel: longPressTouchCancel,
      onPointerDown: longPressPointerDown,
      onPointerMove: longPressPointerMove,
      onPointerUp: longPressPointerUp,
      onPointerCancel: longPressPointerCancel,
      onContextMenu,
      onClickCapture,
    } = longPressHandlers;

    const onTouchStart = (e) => {
      if (swipeEnabled) {
        swipeTouchStart(e);
      }
      if (navigatorEnabled) {
        longPressTouchStart(e);
      }
    };

    const onTouchMove = (e) => {
      if (swipeEnabled) {
        swipeTouchMove(e);
      }
      if (navigatorEnabled) {
        longPressTouchMove(e);
      }
    };

    const onTouchEnd = (e) => {
      if (swipeEnabled) {
        swipeTouchEnd(e);
      }
      if (navigatorEnabled) {
        longPressTouchEnd(e);
      }
    };

    const onTouchCancel = (e) => {
      if (swipeEnabled) {
        swipeTouchCancel(e);
      }
      if (navigatorEnabled) {
        longPressTouchCancel(e);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    if (swipeEnabled) {
      el.addEventListener("touchmove", onTouchMove, { passive: false });
    } else {
      el.addEventListener("touchmove", onTouchMove, { passive: true });
    }
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchCancel, { passive: true });

    if (navigatorEnabled) {
      el.addEventListener("contextmenu", onContextMenu);
      el.addEventListener("pointerdown", longPressPointerDown, { passive: true });
      el.addEventListener("pointermove", longPressPointerMove, { passive: true });
      el.addEventListener("pointerup", longPressPointerUp, { passive: true });
      el.addEventListener("pointercancel", longPressPointerCancel, {
        passive: true,
      });
      document.addEventListener("click", onClickCapture, true);
    }

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchCancel);

      if (navigatorEnabled) {
        el.removeEventListener("contextmenu", onContextMenu);
        el.removeEventListener("pointerdown", longPressPointerDown);
        el.removeEventListener("pointermove", longPressPointerMove);
        el.removeEventListener("pointerup", longPressPointerUp);
        el.removeEventListener("pointercancel", longPressPointerCancel);
        document.removeEventListener("click", onClickCapture, true);
      }
    };
  }, [
    swipeHandlers,
    longPressHandlers,
    swipeEnabled,
    navigatorEnabled,
    gesturesActive,
  ]);

  return {
    ref,
    style: swipeEnabled ? swipeHandlers.touchActionStyle : undefined,
  };
}
